import { Box, Skeleton, Stack, Typography } from "@mui/material";

export const STAT_CARD_MIN_HEIGHT = 120;

export type StatCardProps = {
  value: string;
  caption: string;
  subCaption?: string;
  /** A second sub-caption line, rendered below `subCaption` when present.
   * A distinct prop (rather than folding into `subCaption`) so the two lines
   * can carry different colors. */
  extraCaption?: string;
  valueColor?: string;
  subCaptionColor?: string;
  extraCaptionColor?: string;
};

/**
 * A single headline number with its label beneath it, on a soft grey tile —
 * the KPI card used across the waste dashboards.
 *
 * Distinct from `StatDisplay`, which puts the label ABOVE the value; this is the
 * value-first variant. The shared grey tile box centers its text and fixes a
 * tighter corner radius, so these carry their own box with the same grey to get
 * left-aligned data, vertical centering, and softer corners.
 */
export const StatCard = ({
  value,
  caption,
  subCaption,
  extraCaption,
  valueColor = "text.primary",
  subCaptionColor = "text.secondary",
  extraCaptionColor = "text.secondary",
}: StatCardProps) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      minHeight: STAT_CARD_MIN_HEIGHT,
      px: 2,
      py: 1,
      backgroundColor: (theme) => theme.palette.grey50.main,
      borderRadius: 3,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Stack spacing={0.5}>
      <Typography variant="h4" color={valueColor}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
      {subCaption && (
        <Typography variant="caption" color={subCaptionColor}>
          {subCaption}
        </Typography>
      )}
      {extraCaption && (
        <Typography variant="caption" color={extraCaptionColor}>
          {extraCaption}
        </Typography>
      )}
    </Stack>
  </Box>
);

/** Matching placeholders so a loading row keeps the cards' footprint. */
export const StatCardRowSkeleton = ({ count }: { count: number }) => (
  <Stack direction="row" spacing={2}>
    {Array.from({ length: count }, (_, index) => (
      <Skeleton key={index} variant="rounded" height={STAT_CARD_MIN_HEIGHT} sx={{ flex: 1 }} />
    ))}
  </Stack>
);
